import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Filter, TrendingUp, Clock, CheckCircle, AlertCircle } from 'react-feather';
import api from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { 
  Plus, 
  Droplet, 
  MoreVertical, 
  MapPin, 
  Calendar, 
  User, 
  Phone,
  Home,
  Loader,
  Users,
  Check,
  X,
  ThumbsUp,
  ThumbsDown,
  Image,
  Heart,
  Shield,
  Star,
  Eye,
  Edit,
  Trash2
} from 'react-feather';
import { useNavigate } from 'react-router-dom';

// Fallback data for active requests
const dummyActiveRequests = [
  {
    id: 'REQ001',
    bloodGroup: 'A+',
    units: 2,
    urgencyLevel: 'Critical',
    status: 'Pending',
    patientName: 'John Doe',
    patientPhone: '+91 98765-43210',
    State: 'Maharashtra',
    City:'mumbai',
    Location: 'mumbai',
    createdAt: new Date().toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    createdAtDate: new Date(),
    donors: []
  }
];

const ActiveRequest = () => {
  const { user } = useAuth();
  const [activeRequests, setActiveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDonationConfirmationModal, setShowDonationConfirmationModal] = useState(false);
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [donationFeedback, setDonationFeedback] = useState('');
  const [updatingDonationStatus, setUpdatingDonationStatus] = useState(false);
  const [rejectionType, setRejectionType] = useState('complete'); // 'complete' or 'reupload'
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Current date as default
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const navigate = useNavigate();

  // Get current location when component mounts
  useEffect(() => {
    getCurrentLocation();
  }, []);

  // Fetch active requests when component mounts or user changes
  useEffect(() => {
    if (user) {
      fetchActiveRequests();
    } else {
      setActiveRequests(dummyActiveRequests);
      setLoading(false);
    }
  }, [user]);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          const { latitude, longitude } = position.coords;
          setCurrentLocation({ lat: latitude, lng: longitude });
        },
        error => {
          console.error('Error getting location:', error);
          toast.error('Unable to get your current location. Some features may be limited.');
        }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
    }
  };

  const fetchActiveRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found. Please log in again.');
        setLoading(false);
        return;
      }

      // Fetch active requests from API
      const response = await api.get('/requests/me');
      console.log('Active requests:', response.data);
      

      
      // Process the data to include donation confirmations
      const processedRequests = response.data.map(request => {
        // Check if any donors have confirmed their donation
        const confirmedDonors = request.donors ? request.donors.filter(donor => 
          donor.donationProofPhoto && donor.status !== 'cancelled'
        ) : [];
        
        // Count accepted donors
        const acceptedDonors = request.donors ? request.donors.filter(donor => 
          donor.status === 'accepted' || donor.status === 'donated' || donor.status === 'pending_confirmation'
        ) : [];
        
        // Format the creation date
        const createdDate = new Date(request.createdAt);
        const formattedDate = createdDate.toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
        
        const formattedTime = createdDate.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit'
        });
        
        return {
          id: request.id || request._id, // Ensure id is always available
          ...request,
          confirmedDonors,
          acceptedDonors,
          acceptedDonorsCount: request.acceptedDonorsCount || acceptedDonors.length,
          maxDonorsReached: request.maxDonorsReached || acceptedDonors.length >= 3,
          formattedDate,
          formattedTime
        };
      });
      
      // Sort requests with the latest ones at the top
      const sortedRequests = processedRequests.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB - dateA; // Sort in descending order (newest first)
      });
      
      setActiveRequests(sortedRequests);
    } catch (error) {
      console.error('Error fetching active requests:', error);
      setError('Failed to load your active requests. Please try again.');
      toast.error('Failed to load your active requests');
      // Use dummy data as fallback
      setActiveRequests(dummyActiveRequests);
    } finally {
      setLoading(false);
    }
  };

  const handleNewRequestSuccess = (newRequest) => {
    setActiveRequests(prev => [newRequest, ...prev]);
    toast.success('Blood request created successfully!');
  };

  // Open donation confirmation modal
  const openDonationConfirmationModal = (request, donor) => {
    console.log('Opening donation confirmation modal with donor:', donor);
    
    // Extract the donor ID correctly, handling all possible formats
    const donorId = typeof donor.donor === 'object' ? donor.donor._id : 
                   (donor.donor || donor.donorId || donor._id);
                   
    console.log('Extracted donorId:', donorId);
    
    setSelectedRequest(request);
    setSelectedDonation({
      donorId: donorId,
      donorName: donor.donorName || 'Donor',
      donorEmail: donor.donorEmail || 'Not provided',
      donorPhone: donor.donorPhone || 'Not provided',
      acceptedDate: donor.acceptedDate ? new Date(donor.acceptedDate).toLocaleDateString() : 'Not recorded',
      donationProofPhoto: donor.donationProofPhoto || '',
      notes: donor.notes || '',
      donationDate: donor.donationDate || new Date().toISOString()
    });
    setDonationFeedback('');
    setShowDonationConfirmationModal(true);
  };

  // Close donation confirmation modal
  const closeDonationConfirmationModal = () => {
    setShowDonationConfirmationModal(false);
    setSelectedRequest(null);
    setSelectedDonation(null);
    setDonationFeedback('');
  };

  // Update donation status (confirm or reject)
  const updateDonationStatus = async (status) => {
    if (!selectedRequest || !selectedDonation) return;
    
    setUpdatingDonationStatus(true);
    
    try {
      const requestId = selectedRequest.id || selectedRequest._id;
      console.log('Updating donation status with:', {
        requestId,
        donorId: selectedDonation.donorId,
        donationStatus: status,
        feedback: donationFeedback
      });
      
      const response = await api.put(`/requests/${requestId}/donation-status`, {
        donorId: selectedDonation.donorId,
        donationStatus: status,
        feedback: donationFeedback
      });
      
      console.log('Donation status update response:', response.data);
      
      // Close the modal first before updating state to prevent errors during navigation
      closeDonationConfirmationModal();
      
      // Show success message
      if (status === 'confirmed') {
        toast.success(
          <div className="text-center">
            <div className="font-bold text-lg mb-1">Donation Verified Successfully!</div>
            <div>The request has been marked as fulfilled and other donors have been notified.</div>
          </div>
        );
      } else {
        toast.success(
          status === 'confirmed' 
            ? 'Donation confirmed successfully!' 
            : status === 'reupload'
              ? 'Reupload requested successfully!'
              : 'Donation rejected successfully!'
        );
      }
      
      // Refresh the requests to update the UI
      fetchActiveRequests();
    } catch (error) {
      console.error('Error updating donation status:', error);
      setUpdatingDonationStatus(false);
      
      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please log in again.');
        return;
      }
      
      toast.error(error.response?.data?.message || 'Failed to update donation status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to get blood group from multiple possible field names
  const getBloodGroup = (request) => {
    const bloodGroup = request.bloodType || 
                      request.bloodGroup || 
                      request.blood_group || 
                      request.blood_type || 
                      request.bloodGroupNeeded ||
                      request.requiredBloodGroup ||
                      request.bloodGroupRequired ||
                      request.blood_type_required ||
                      request.blood_group_required;
    
    return bloodGroup || 'Not specified';
  };

  // Handle donation confirmation with confirmation dialog
  const handleConfirmDonation = () => {
    setPendingAction('confirm');
    setShowConfirmationDialog(true);
  };
  
  // Handle donation rejection with options
  const handleRejectDonation = (type) => {
    setRejectionType(type);
    setPendingAction('reject');
    setShowConfirmationDialog(true);
  };
  
  // Execute the pending action after confirmation
  const executePendingAction = () => {
    if (pendingAction === 'confirm') {
      updateDonationStatus('confirmed');
    } else if (pendingAction === 'reject') {
      updateDonationStatus(rejectionType === 'complete' ? 'rejected' : 'reupload');
    }
    setShowConfirmationDialog(false);
    setPendingAction(null);
  };
  
  // Cancel the pending action
  const cancelPendingAction = () => {
    setShowConfirmationDialog(false);
    setPendingAction(null);
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'fulfilled':
        return 'bg-gradient-to-r from-green-500 to-green-600';
      case 'pending':
        return 'bg-gradient-to-r from-yellow-500 to-orange-500';
      case 'active':
        return 'bg-gradient-to-r from-blue-500 to-blue-600';
      default:
        return 'bg-gradient-to-r from-gray-500 to-gray-600';
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency?.toLowerCase()) {
      case 'critical':
        return 'bg-gradient-to-r from-red-500 to-red-600';
      case 'high':
        return 'bg-gradient-to-r from-orange-500 to-orange-600';
      default:
        return 'bg-gradient-to-r from-yellow-500 to-yellow-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'fulfilled':
        return <CheckCircle className="w-5 h-5" />;
      case 'pending':
        return <Clock className="w-5 h-5" />;
      case 'active':
        return <TrendingUp className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const filteredRequests = activeRequests.filter(request => {
    // Status filter
    const statusMatch = selectedStatus === 'all' || request.status.toLowerCase() === selectedStatus.toLowerCase();
    
    // Date filter
    let dateMatch = true;
    if (selectedDate) {
      const requestDate = new Date(request.createdAt);
      const selectedDateObj = new Date(selectedDate);
      dateMatch = requestDate.toDateString() === selectedDateObj.toDateString();
    }
    
    return statusMatch && dateMatch;
  });

  const requestCounts = {
    all: activeRequests.length,
    fulfilled: activeRequests.filter(r => r.status.toLowerCase() === 'fulfilled').length,
    pending: activeRequests.filter(r => r.status.toLowerCase() === 'pending').length,
    active: activeRequests.filter(r => r.status.toLowerCase() === 'active').length
  };

  // Get today's date for the date picker
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Header Section */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 sm:gap-3 mb-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                  <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Active Requests</h1>
                  <p className="text-sm sm:text-base text-gray-600">Manage your blood donation requests and track donor responses</p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${
                    viewMode === 'grid' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Grid
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${
                    viewMode === 'list' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  List
                </button>
              </div>
              
              {/* Filter Button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all"
              >
                <Filter size={14} className="sm:w-4 sm:h-4" />
                Filter
                <span className={`w-2 h-2 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </button>
              
              {/* New Request Button */}
              <button 
                onClick={() => navigate('/requester-dashboard/create')}
                className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 flex items-center gap-2 font-medium shadow-lg hover:shadow-xl transform hover:scale-105 text-sm sm:text-base"
              >
                <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                New Request
              </button>
            </div>
          </div>

          {/* Filter Options */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden mt-4 sm:mt-6"
              >
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 sm:p-6 rounded-xl border border-gray-200">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Filter Requests</h3>
                  
                  {/* Date Filter */}
                  <div className="mb-4 sm:mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Date</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        max={today}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      />
                      <button
                        onClick={() => setSelectedDate(today)}
                        className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-xs sm:text-sm font-medium"
                      >
                        Today
                      </button>
                      <button
                        onClick={() => setSelectedDate('')}
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs sm:text-sm font-medium"
                      >
                        All Dates
                      </button>
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
                    <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
                      <button
                        onClick={() => setSelectedStatus('all')}
                        className={`px-3 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-medium transition-all ${
                          selectedStatus === 'all'
                            ? 'bg-gray-900 text-white shadow-lg'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                        }`}
                      >
                        All ({requestCounts.all})
                      </button>
                      <button
                        onClick={() => setSelectedStatus('active')}
                        className={`px-3 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-medium transition-all ${
                          selectedStatus === 'active'
                            ? 'bg-blue-600 text-white shadow-lg'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                        }`}
                      >
                        Active ({requestCounts.active})
                      </button>
                      <button
                        onClick={() => setSelectedStatus('pending')}
                        className={`px-3 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-medium transition-all ${
                          selectedStatus === 'pending'
                            ? 'bg-yellow-600 text-white shadow-lg'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                        }`}
                      >
                        Pending ({requestCounts.pending})
                      </button>
                      <button
                        onClick={() => setSelectedStatus('fulfilled')}
                        className={`px-3 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-medium transition-all ${
                          selectedStatus === 'fulfilled'
                            ? 'bg-green-600 text-white shadow-lg'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                        }`}
                      >
                        Fulfilled ({requestCounts.fulfilled})
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-12 text-center">
            <div className="animate-spin w-12 h-12 sm:w-16 sm:h-16 border-4 border-gray-200 border-t-red-500 rounded-full mx-auto mb-4 sm:mb-6"></div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Loading your requests</h3>
            <p className="text-sm sm:text-base text-gray-600">Please wait while we fetch your active blood donation requests...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-12 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <AlertCircle className="h-8 w-8 sm:h-10 sm:w-10 text-red-500" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">Something went wrong</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 max-w-md mx-auto">{error}</p>
            <button 
              onClick={fetchActiveRequests}
              className="bg-gradient-to-r from-red-500 to-red-600 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl text-sm sm:text-base"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Active Requests Grid/List */}
        {!loading && !error && filteredRequests.length > 0 && (
          <div className={`${
            viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6' 
              : 'space-y-4 sm:space-y-6'
          }`}>
            {filteredRequests.map((request, index) => (
              <motion.div
                key={request.id || request._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 ${
                  viewMode === 'list' ? 'flex flex-col sm:flex-row' : ''
                }`}
              >
                {/* Status Header */}
                <div className={`px-4 sm:px-6 py-3 sm:py-4 ${getStatusColor(request.status)} text-white`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3">
                      {getStatusIcon(request.status)}
                      <div>
                        <h3 className="font-semibold text-base sm:text-lg">{request.status}</h3>
                        <p className="text-xs sm:text-sm opacity-90">ID: {(request.id || request._id || '').toString().substring(0, 8)}</p>
                      </div>
                    </div>
                    
                    {/* Donor Count Badge */}
                    <div className="bg-white bg-opacity-20 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex items-center gap-1 sm:gap-2">
                      <Users className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="font-medium text-xs sm:text-sm">
                        {request.acceptedDonorsCount || 0}/3
                      </span>
                    </div>
                  </div>
                </div>

                {/* Request Content */}
                <div className={`p-4 sm:p-6 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                  <div className={`${viewMode === 'list' ? 'flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6' : ''}`}>
                    {/* Blood Group Section */}
                    <div className={`${viewMode === 'list' ? 'flex-shrink-0' : 'mb-4 sm:mb-6'}`}>
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-red-100 to-red-200 rounded-2xl flex items-center justify-center">
                        <Droplet className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
                      </div>
                      <div className="mt-2 sm:mt-3 text-center">
                        <h4 className="text-xl sm:text-2xl font-bold text-gray-900">{getBloodGroup(request)}</h4>
                        <p className="text-xs sm:text-sm text-gray-600">Blood Group</p>
                      </div>
                    </div>

                    {/* Request Details */}
                    <div className={`${viewMode === 'list' ? 'flex-1' : ''}`}>
                      <div className="space-y-3 sm:space-y-4">
                                                {/* Patient Info */}
                        <div>
                          <h4 className="font-semibold text-gray-900 text-base sm:text-lg mb-2">Patient Information</h4>
                          <div className="bg-gray-50 rounded-xl p-3 sm:p-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <User className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
                                <span className="text-xs sm:text-sm font-medium">{request.patientName}</span>
                              </div>
                              <div className="flex items-center gap-2 sm:gap-3">
                                <Droplet className="w-3 h-3 sm:w-4 sm:h-4 text-red-500" />
                                <span className="text-xs sm:text-sm font-medium">Blood Group: {getBloodGroup(request)}</span>
                              </div>
                             
                            </div>
                          </div>
                        </div>

                        {/* Request Details */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                          <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-blue-50 rounded-xl">
                            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                            <div>
                              <p className="text-xs sm:text-sm font-medium text-gray-900">
                                {request.formattedDate || formatDate(request.createdAt)}
                              </p>
                              <p className="text-xs text-gray-600">Created</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-orange-50 rounded-xl">
                            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                            <div>
                              <p className="text-xs sm:text-sm font-medium text-gray-900">
                                {request.unitsNeeded || request.units} {(request.unitsNeeded || request.units) > 1 ? 'units' : 'unit'}
                              </p>
                              <p className="text-xs text-gray-600">Required</p>
                            </div>
                          </div>
                        </div>

                        {/* Urgency and Location */}
                        <div className="space-y-2 sm:space-y-3">
                          <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-red-50 rounded-xl">
                            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                            <div className="flex-1">
                              <p className="text-xs sm:text-sm font-medium text-gray-900">
                                {request.urgencyLevel || 'Normal'} Priority
                              </p>
                              <p className="text-xs text-gray-600">Urgency Level</p>
                            </div>
                            <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium text-white ${getUrgencyColor(request.urgencyLevel)}`}>
                              {request.urgencyLevel || 'Normal'}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-green-50 rounded-xl">
                            <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                            <div className="flex-1">
                              <p className="text-xs sm:text-sm font-medium text-gray-900">
                                {request.Location || (request.hospital && request.hospital.name) || 'Location not specified'}
                              </p>
                              <p className="text-xs text-gray-600">Hospital/Location</p>
                            </div>
                          </div>
                        </div>

                        {/* Donors Section */}
                        {request.donors && request.donors.length > 0 && (
                          <div className="mt-4 sm:mt-6">
                            <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2">
                              <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                              Donors ({request.donors.length})
                            </h4>
                            <div className="space-y-2 sm:space-y-3">
                              {request.donors.map((donor, index) => (
                                <div key={index} className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-100">
                                  <div className="flex items-center justify-between mb-2 sm:mb-3">
                                    <div className="flex items-center gap-2 sm:gap-3">
                                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center">
                                        <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                                          {donor.donorName || (donor.donor && typeof donor.donor === 'object' ? donor.donor.name : 'Donor')}
                                        </p>
                                        <div className="flex items-center gap-1 sm:gap-2 text-xs text-gray-500">
                                          <Phone className="w-3 h-3" />
                                          <a href={`tel:${donor.donorPhone || (donor.donor && donor.donor.phone)}`} className="hover:text-red-600">
                                            {donor.donorPhone || (donor.donor && donor.donor.phone) || 'No phone'}
                                          </a>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${
                                      donor.status === 'accepted' ? 'bg-green-100 text-green-700' : 
                                      donor.status === 'donated' ? 'bg-blue-100 text-blue-700' : 
                                      donor.status === 'cancelled' ? 'bg-red-100 text-red-700' : 
                                      'bg-yellow-100 text-yellow-700'
                                    }`}>
                                      {donor.status === 'accepted' ? 'Accepted' : 
                                       donor.status === 'donated' ? 'Donated' : 
                                       donor.status === 'cancelled' ? 'Cancelled' : 'Pending'}
                                    </span>
                                  </div>
                                  
                                  {donor.acceptedDate && (
                                    <div className="text-xs text-gray-500 mb-2 sm:mb-3">
                                      <Calendar className="w-3 h-3 inline mr-1" />
                                      <span>Accepted: {new Date(donor.acceptedDate).toLocaleDateString('en-IN', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}</span>
                                    </div>
                                  )}
                                  
                                  {/* Action Buttons */}
                                  <div className="flex gap-2">
                                    {donor.donationProofPhoto && donor.status !== 'donated' && donor.status !== 'rejected' && (
                                      <button
                                        onClick={() => openDonationConfirmationModal(request, donor)}
                                        className="flex-1 px-2 sm:px-3 py-2 bg-green-100 text-green-700 rounded-lg text-xs font-medium flex items-center justify-center gap-1 hover:bg-green-200 transition-colors"
                                      >
                                        <Image className="w-3 h-3" />
                                        Verify Donation
                                      </button>
                                    )}
                                    
                                    {donor.status === 'donated' && (
                                      <span className="flex-1 px-2 sm:px-3 py-2 bg-green-100 text-green-700 rounded-lg text-xs font-medium flex items-center justify-center gap-1">
                                        <Check className="w-3 h-3" />
                                        Verified
                                      </span>
                                    )}

                                    {donor.status === 'rejected' && (
                                      <span className="flex-1 px-2 sm:px-3 py-2 bg-red-100 text-red-700 rounded-lg text-xs font-medium flex items-center justify-center gap-1">
                                        <X className="w-3 h-3" />
                                        Rejected
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredRequests.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-2xl shadow-lg p-6 sm:p-12 text-center"
          >
            <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <Heart className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">No Active Requests</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 max-w-md mx-auto">
              You don't have any active blood donation requests at the moment. Create a new request to get started.
            </p>
            <button 
              onClick={() => navigate('/requester-dashboard/create')}
              className="bg-gradient-to-r from-red-500 to-red-600 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl flex items-center gap-2 mx-auto text-sm sm:text-base"
            >
              <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
              Create New Request
            </button>
          </motion.div>
        )}

        {/* Donation Confirmation Modal */}
        {showDonationConfirmationModal && selectedRequest && selectedDonation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Verify Blood Donation</h3>
                    <p className="text-gray-600">Please confirm if you received this blood donation</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8 space-y-8">
                {/* Donation Details */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-2xl">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Droplet className="w-5 h-5 text-blue-600" />
                    Donation Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white p-4 rounded-xl">
                      <p className="text-sm text-gray-500 mb-1">Blood Type</p>
                      <p className="font-semibold text-gray-900 text-lg">{getBloodGroup(selectedRequest)}</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl">
                      <p className="text-sm text-gray-500 mb-1">Hospital</p>
                      <p className="font-semibold text-gray-900">{selectedRequest.hospital ? selectedRequest.hospital.name : 'Not specified'}</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl">
                      <p className="text-sm text-gray-500 mb-1">Donation Date</p>
                      <p className="font-semibold text-gray-900">{formatDate(selectedDonation.donationDate)}</p>
                    </div>
                  </div>
                </div>
                
                {/* Donor Information */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-2xl">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-green-600" />
                    Donor Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white p-4 rounded-xl">
                      <p className="text-sm text-gray-500 mb-1">Name</p>
                      <p className="font-semibold text-gray-900">{selectedDonation.donorName}</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl">
                      <p className="text-sm text-gray-500 mb-1">Email</p>
                      <p className="font-semibold text-gray-900">{selectedDonation.donorEmail}</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl">
                      <p className="text-sm text-gray-500 mb-1">Phone</p>
                      <p className="font-semibold text-gray-900">{selectedDonation.donorPhone}</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl">
                      <p className="text-sm text-gray-500 mb-1">Request Accepted On</p>
                      <p className="font-semibold text-gray-900">{selectedDonation.acceptedDate}</p>
                    </div>
                  </div>
                </div>
                
                {/* Donation Photo */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Image className="w-5 h-5 text-gray-600" />
                    Donation Proof Photo
                  </h4>
                  {selectedDonation.donationProofPhoto ? (
                    <div className="border-2 border-gray-200 rounded-2xl overflow-hidden">
                      <img 
                        src={selectedDonation.donationProofPhoto} 
                        alt="Donation Proof" 
                        className="w-full h-auto max-h-[400px] object-contain"
                      />
                    </div>
                  ) : (
                    <div className="bg-gray-100 rounded-2xl p-12 text-center">
                      <Image className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No photo provided</p>
                    </div>
                  )}
                </div>
                
                {/* Donor's Notes */}
                {selectedDonation.notes && (
                  <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-6 rounded-2xl">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-600" />
                      Donor's Notes
                    </h4>
                    <div className="bg-white p-4 rounded-xl">
                      <p className="text-gray-700 italic text-lg">"{selectedDonation.notes}"</p>
                    </div>
                  </div>
                )}
                
                {/* Feedback */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Edit className="w-5 h-5 text-gray-600" />
                    Your Feedback (Optional)
                  </h4>
                  <textarea
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    rows="4"
                    placeholder="Add your feedback or thank you note for the donor..."
                    value={donationFeedback}
                    onChange={(e) => setDonationFeedback(e.target.value)}
                  ></textarea>
                </div>
              </div>
              
              <div className="p-8 border-t border-gray-100 bg-gray-50">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <button
                    onClick={closeDonationConfirmationModal}
                    className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDonation}
                    className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-200 font-medium flex items-center justify-center gap-2"
                  >
                    <ThumbsUp className="w-4 h-4" />
                    Confirm Donation
                  </button>
                  <button
                    onClick={() => handleRejectDonation('complete')}
                    className="w-full px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 font-medium flex items-center justify-center gap-2"
                  >
                    <ThumbsDown className="w-4 h-4" />
                    Reject
                  </button>
                  <button
                    onClick={() => handleRejectDonation('reupload')}
                    className="w-full px-6 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-xl hover:from-yellow-600 hover:to-yellow-700 transition-all duration-200 font-medium flex items-center justify-center gap-2"
                  >
                    <Image className="w-4 h-4" />
                    Request Reupload
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        {showConfirmationDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {pendingAction === 'confirm' ? 'Confirm Donation' : 
                   rejectionType === 'complete' ? 'Reject Donation' : 'Request Image Reupload'}
                </h3>
                
                <p className="text-gray-600">
                  {pendingAction === 'confirm' 
                    ? 'Are you sure you want to confirm this donation? This action cannot be undone.'
                    : rejectionType === 'complete'
                      ? 'Are you sure you want to reject this donation? This action cannot be undone.'
                      : 'Are you sure you want to request the donor to reupload a different image? The donor will be notified.'}
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={cancelPendingAction}
                  className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                
                <button
                  onClick={executePendingAction}
                  className={`flex-1 py-3 text-white rounded-xl transition-all duration-200 font-medium ${
                    pendingAction === 'confirm' 
                      ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700' 
                      : rejectionType === 'complete'
                        ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                        : 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700'
                  }`}
                >
                  {pendingAction === 'confirm' ? 'Yes, Confirm' : 
                   rejectionType === 'complete' ? 'Yes, Reject' : 'Yes, Request Reupload'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveRequest;
